import { useState } from "react";

import {
  CopyToClipboardButton,
  DefaultButton,
  ScrollablePane,
  ScrollbarVisibility,
  Stack,
  TextBadge,
  TextBadgeColor,
  TextBadgeSize
} from "@bps/fluent-ui";
import { DateTimeWithDurationSinceTooltip } from "@components/DateTimeWithDurationSinceTooltip";
import { TableGuidDisplay } from "@components/TableGuidDisplay";
import { DataTable, DataTableColumn } from "@components/tables/DataTable";
import { FieldDeviceIdentifier } from "@libs/api/gateways/field/field-ops-gateway.dtos";
import { useMostRecentFieldDeviceIdentifiers } from "@libs/api/gateways/field/field-ops-gateway.hooks";

import { IdentifierHistoryModal } from "./history/IdentifierHistoryModal";
import { IdentifiersFilterValues } from "./IdentifiersFilter";

interface IdentifiersTableProps {
  filter: IdentifiersFilterValues;
  deviceId: string;
}

const filterAndSortIdentifiers = (
  identifiers: FieldDeviceIdentifier[] | undefined,
  filter: IdentifiersFilterValues
): FieldDeviceIdentifier[] => {
  if (!identifiers) return [];

  return identifiers
    .filter(d => {
      const containsAuthenticatedFilter =
        filter.authenticated.length === 0
          ? true
          : filter.authenticated.filter(
              filterVal => String(d.authenticated) === filterVal
            ).length > 0;

      const noFiltersApplied = filter.authenticated.length === 0;

      if (containsAuthenticatedFilter || noFiltersApplied) {
        return (
          filter.name === "" ||
          d.identifierType.toLowerCase().indexOf(filter.name.toLowerCase()) >=
            0 ||
          (d.identifierValue &&
            d.identifierValue
              .toLowerCase()
              .indexOf(filter.name.toLowerCase()) >= 0)
        );
      }

      return false;
    })
    .sort((a: FieldDeviceIdentifier, b: FieldDeviceIdentifier) => {
      return a.identifierType < b.identifierType ? 1 : -1;
    });
};

export const IdentifiersTable = ({
  filter,
  deviceId
}: IdentifiersTableProps) => {
  const identifiersQuery = useMostRecentFieldDeviceIdentifiers(deviceId);
  const { data, isLoading, error } = identifiersQuery;
  const errorMessage = error?.message;

  const items = filterAndSortIdentifiers(data, filter);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedIdentifier, setSelectedIdentifier] = useState<
    FieldDeviceIdentifier
  >();

  const onShowIdentifierHistory = (identifier: FieldDeviceIdentifier) => {
    setSelectedIdentifier(identifier);
    setShowHistory(true);
  };

  const onDismiss = () => {
    setShowHistory(false);
    setSelectedIdentifier(undefined);
  };

  const columns: DataTableColumn<FieldDeviceIdentifier>[] = [
    {
      key: "identifierType",
      name: "Name",
      minWidth: 180,
      maxWidth: 300,
      isResizable: true,
      isSorted: true,
      sort: true,
      onRender: (identifier: FieldDeviceIdentifier) => (
        <CopyToClipboardButton
          clipboardContent={identifier.identifierType}
          text={identifier.identifierType}
        />
      )
    },
    {
      key: "identifierValue",
      name: "Value",
      minWidth: 220,
      isResizable: true,
      onRender: (identifier: FieldDeviceIdentifier) =>
        identifier.identifierValue && (
          <CopyToClipboardButton
            clipboardContent={identifier.identifierValue}
            text={identifier.identifierValue}
          />
        )
    },
    {
      key: "lastObservedUtc",
      name: "Last Observed",
      minWidth: 150,
      onRender: (identifier: FieldDeviceIdentifier) => (
        <DateTimeWithDurationSinceTooltip
          dateUtc={identifier.lastObservedUtc}
        />
      )
    },
    {
      key: "prevObservedUtc",
      name: "Previous Observed",
      minWidth: 150,
      onRender: (identifier: FieldDeviceIdentifier) => (
        <DateTimeWithDurationSinceTooltip
          dateUtc={identifier.prevObservedUtc}
        />
      )
    },
    {
      key: "lastSourceId",
      name: "Last Source ID",
      minWidth: 150,
      onRender: (identifier: FieldDeviceIdentifier) => (
        <TableGuidDisplay id={identifier.lastSourceId} />
      )
    },
    {
      key: "lastSourceType",
      name: "Last Source Type",
      onRender: (identifier: FieldDeviceIdentifier) => identifier.lastSourceType
    },
    {
      key: "observationCount",
      name: "Observation Count",
      onRender: (identifier: FieldDeviceIdentifier) =>
        identifier.observationCount
    },
    {
      key: "authenticated",
      name: "Authenticated",
      onRender: (identifier: FieldDeviceIdentifier) => {
        const authenticatedLabel = identifier.authenticated
          ? "Authenticated"
          : "Not Authenticated";

        return (
          <TextBadge
            hasBorder={true}
            badgeSize={TextBadgeSize.small}
            badgeColor={
              identifier.authenticated
                ? TextBadgeColor.green
                : TextBadgeColor.red
            }
          >
            {authenticatedLabel}
          </TextBadge>
        );
      }
    },
    {
      key: "history",
      name: "History",
      onRender: (identifier: FieldDeviceIdentifier) => {
        return (
          <DefaultButton onClick={() => onShowIdentifierHistory(identifier)}>
            Show History
          </DefaultButton>
        );
      }
    }
  ];

  return (
    <Stack grow styles={{ root: { position: "relative" } }} verticalFill>
      <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
        <DataTable
          stickyHeader
          shimmerLines={10}
          items={items}
          columns={columns}
          onShouldVirtualize={() => true}
          enableShimmer={isLoading}
          errorMessage={errorMessage}
        />
        {selectedIdentifier && (
          <IdentifierHistoryModal
            identifier={selectedIdentifier}
            show={showHistory}
            onDismiss={onDismiss}
          />
        )}
      </ScrollablePane>
    </Stack>
  );
};
